import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { TableController } from '../../../components/Constant'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import { USER_REPORTS } from '../../../repository/revenue_reports_repo'
import { Sector } from 'recharts'
import toast from 'react-hot-toast'
import moment from 'moment-timezone'

export const useUserReport = () => {
    const { t } = useTranslation()
    const [controller, setController] = useState(TableController)
    const [loader, setLoader] = useState(false)
    const [totalCount, setTotalCount] = useState(0)
    const location = useLocation()
    const [details, setDetails] = useState([])
    const [otherInformation, setOtherInformation] = useState([])
    const [filter, setFilter] = useState({})

    const handlePagination = (obj) => setController(obj)

    const reportKey = useMemo(() => {
        const pathSegments = location.pathname.split('/').filter(Boolean)
        const lastSegment = pathSegments[pathSegments.length - 1]
        return lastSegment
    }, [location.pathname])

    const [filterInputs, setFilterInputs] = useState({ startDate: null, endDate: null, filter: { name: 'Year', value: 'YEAR' } })
    const filterRef = useRef(filterInputs)
    const getUserReport = useCallback(
        async (isDownload) => {
            setLoader(true)
            let payload = {
                type: reportKey,
                startDate: moment(filterRef?.current?.startDate).startOf('day'),
                endDate: moment(filterRef?.current?.endDate).endOf('day'),
                filter: filterRef?.current?.filter?.value,
                isDownloadExcel: isDownload,
                page: controller.page,
                count: controller.rowsPerPage,
            }
            try {
                let response = await USER_REPORTS(payload)
                if (isDownload === false) {
                    setDetails(response.data)
                    setOtherInformation(response.otherInfo)
                    setTotalCount(response.totalCount)
                } else {
                    window.open(response?.data)
                }
                setLoader(false)
            } catch (error) {
                setDetails([])
                setLoader(false)
                toast.error(error.message)
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        },
        [controller, reportKey]
    )

    useEffect(() => {
        filterRef.current = filterInputs
    }, [filterInputs])

    useEffect(() => {
        getUserReport(false)
    }, [getUserReport])

    const totalRegidteredUserTableHead = [{ label: '#' }, { label: `${t('tableTitle.name')}` }, { label: `${t('formTitle.mobileNumber')}` }, { label: `${t('formTitle.email')}` }]

    const activeInactiveUserTableHead = [
        { label: '#' },
        { label: `${t('tableTitle.name')}` },
        { label: `${t('formTitle.mobileNumber')}` },
        { label: `${t('formTitle.email')}` },
        { label: `${t('tableTitle.status')}` },
    ]

    const topEngagedUserTableHead = [
        { label: '#' },
        { label: `${t('tableTitle.name')}` },
        { label: `${t('formTitle.mobileNumber')}` },
        { label: `${t('formTitle.email')}` },
        { label: `${t('boxTitle.totalAmount')}` },
    ]

    const renderActiveShape = (props) => {
        const { cx, cy, midAngle, innerRadius, outerRadius, startAngle, endAngle, fill, payload, value } = props

        const RAD = Math.PI / 180
        const angle = -midAngle * RAD

        const rStart = outerRadius + 8
        const rBend = outerRadius + 24

        const cos = Math.cos(angle)
        const sin = Math.sin(angle)

        const sx = cx + rStart * cos
        const sy = cy + rStart * sin

        const mx = cx + rBend * cos
        const my = cy + rBend * sin

        const horizontal = cos >= 0 ? 16 : -16
        const ex = mx + horizontal
        const ey = my

        const textAnchor = cos >= 0 ? 'start' : 'end'
        const stroke = fill || payload?.fill || '#8884d8'
        const labelColor = payload?.color || '#334155'
        const dx = cos >= 0 ? 8 : -8

        return (
            <g>
                <Sector cx={cx} cy={cy} innerRadius={innerRadius} outerRadius={outerRadius} startAngle={startAngle} endAngle={endAngle} fill={fill} />
                <Sector cx={cx} cy={cy} innerRadius={outerRadius + 6} outerRadius={outerRadius + 10} startAngle={startAngle} endAngle={endAngle} fill={fill} />

                <path d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`} stroke={stroke} fill="none" />
                <circle cx={ex} cy={ey} r={2} fill={stroke} stroke="none" />

                <text x={ex} y={ey} dx={dx} textAnchor={textAnchor} fill={labelColor}>
                    {payload?.name ?? ''}
                </text>
                <text x={ex} y={ey} dx={dx} dy={18} textAnchor={textAnchor} fill={labelColor}>
                    {value?.toLocaleString?.() ?? payload?.value ?? ''}
                </text>
            </g>
        )
    }

    const processedData = [
        { name: `ANDROID`, value: details?.ANDROID ?? 0, fill: '#009fb7' },
        { name: `IOS`, value: details?.IOS ?? 0, fill: '#e7b933' },
        { name: `WEB`, value: details?.WEB ?? 0, fill: '#fe4a49' },
    ]

    const getTotalUsers = () => {
        let totalUsers = 0
        totalUsers += details?.ANDROID
        totalUsers += details?.IOS
        totalUsers += details?.WEB
        return totalUsers
    }

    const handleFilterInputChange = (name, value) => {
        setFilterInputs((prev) => ({
            ...prev,
            [name]: name !== 'filter' ? value : value[0],
        }))
    }

    const reset = () => {
        const cleared = { startDate: null, endDate: null, filter: { name: 'Year', value: 'YEAR' } }
        setFilterInputs(cleared)
        filterRef.current = cleared
        getUserReport(false)
    }

    const search = () => getUserReport(false)

    return {
        data: {
            controller,
            loader,
            totalCount,
            details,
            totalRegidteredUserTableHead,
            filter,
            topEngagedUserTableHead,
            activeInactiveUserTableHead,
            otherInformation,
            processedData,
            filterInputs,
        },
        methods: { handlePagination, setLoader, setTotalCount, setFilter, renderActiveShape, getTotalUsers, handleFilterInputChange, reset, search, getUserReport },
    }
}
